import React, { useState } from 'react';
import { Typography, Button, Box } from '@mui/material';
import {
  Home,
  People,
  Build,
  Receipt,
  Description,
  Payment,
  Logout,
  Settings,
  Assessment,
  AccountBalance,
  Business,
  SwapHoriz,
  RequestQuote,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useContext } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import PropertyManagement from '../../components/staff/PropertyManagement';
import TenantManagement from '../../components/staff/TenantManagement';
import RepairManagement from '../../components/staff/RepairManagement';
import InvoiceManagement from '../../components/staff/InvoiceManagement';
import NoticeManagement from '../../components/staff/NoticeManagement';
import PaymentManagement from '../../components/staff/PaymentManagement';
import OtherIncomeManagement from '../../components/staff/OtherIncomeManagement';
import SecurityDeposits from '../../components/staff/SecurityDeposits';
import TenantExitManagement from '../../components/staff/TenantExitManagement';
import RentIncreaseManagement from '../../components/staff/RentIncreaseManagement';
import ContraEntries from '../../components/staff/ContraEntries';
import AccountingDashboard from '../../components/accounting/AccountingDashboard';
import UserSettings from '../../components/UserSettings';
import ExpenseManagement from '../../components/staff/ExpenseManagement';
import PropertyPortfolioDashboard from '../../components/staff/PropertyPortfolioDashboard';
import LandlordManagement from '../../components/staff/LandlordManagement';
import LandlordReports from '../../components/staff/LandlordReports';
import AgencyReports from '../../components/staff/AgencyReports';
import RequisitionManagement from '../../components/staff/RequisitionManagement';
import LandlordPayments from '../../components/staff/LandlordPayments';

// Each tab (other than Settings, which manages the signed-in user's own account and
// is always available) is gated by whether the user's role can view its module.
const ALL_TABS = [
  { icon: <Home />, label: 'Portfolio', module: 'properties', Component: PropertyPortfolioDashboard },
  { icon: <Business />, label: 'Landlords', module: 'landlords', Component: LandlordManagement },
  { icon: <Home />, label: 'Properties', module: 'properties', Component: PropertyManagement },
  { icon: <People />, label: 'Tenants', module: 'tenants', Component: TenantManagement },
  { icon: <People />, label: 'Tenant Exits', module: 'tenantExits', Component: TenantExitManagement },
  { icon: <Receipt />, label: 'Rent Increases', module: 'rentIncreases', Component: RentIncreaseManagement },
  { icon: <Build />, label: 'Repair Records', module: 'repairs', Component: RepairManagement },
  { icon: <Receipt />, label: 'Invoices', module: 'invoices', Component: InvoiceManagement },
  { icon: <Payment />, label: 'Payments', module: 'payments', Component: PaymentManagement },
  { icon: <Receipt />, label: 'Expenses', module: 'expenses', Component: ExpenseManagement },
  // Agency income received against no invoice. Sits beside Expenses rather than Payments,
  // because it belongs to the agency's own books and not to any tenant's account.
  { icon: <Payment />, label: 'Other Income', module: 'otherIncome', Component: OtherIncomeManagement },
  // The deposits register, and the only way to reach a settlement. Under the payments
  // permission, because a deposit is a payment-shaped thing.
  { icon: <Payment />, label: 'Deposits', module: 'payments', Component: SecurityDeposits },
  // Money between the agency's own accounts. Sits near Accounting rather than Payments,
  // because no tenant or landlord is involved in either side of it.
  { icon: <AccountBalance />, label: 'Transfers', module: 'contraEntries', Component: ContraEntries },
  { icon: <RequestQuote />, label: 'Requisitions', module: 'requisitions', Component: RequisitionManagement },
  { icon: <Business />, label: 'Landlord Payments', module: 'landlordPayments', Component: LandlordPayments },
  { icon: <Description />, label: 'Notices', module: 'notices', Component: NoticeManagement },
  { icon: <AccountBalance />, label: 'Accounting', module: 'accounting', Component: AccountingDashboard },
  { icon: <Assessment />, label: 'Agency Reports', module: 'reports', Component: AgencyReports },
  { icon: <Settings />, label: 'Settings', module: null, Component: UserSettings },
  { icon: <Assessment />, label: 'Landlord Reports', module: 'landlordReports', Component: LandlordReports },
];

const StaffDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user, can, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const visibleTabs = ALL_TABS.filter(tab => !tab.module || can(tab.module, 'view'));

  // Wait for the cookie to actually be cleared before leaving, and replace the history
  // entry so Back from the login screen does not step into the signed-out dashboard.
  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <DashboardLayout
      title="Staff Portal"
      navItems={visibleTabs}
      activeIndex={tabValue}
      onSelect={setTabValue}
      headerActions={
        <>
          {user?.role === 'admin' && (
            <Button color="inherit" onClick={() => navigate('/admin')} startIcon={<SwapHoriz />} sx={{ mr: { xs: 0.5, sm: 2 }, minWidth: 0, px: { xs: 1, sm: 2 } }}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Admin Panel</Box>
            </Button>
          )}
          <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
            {user?.name}
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />} sx={{ minWidth: 0, px: { xs: 1, sm: 2 } }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Logout</Box>
          </Button>
        </>
      }
    />
  );
};

export default StaffDashboard;

